import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { LucideIcon } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectGroup {
  id: string;
  label: string;
  options: MultiSelectOption[];
}

interface MultiSelectDropdownProps {
  label: string;
  icon?: LucideIcon;
  options?: MultiSelectOption[];
  groups?: MultiSelectGroup[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function MultiSelectDropdown({
  label,
  icon: Icon,
  options,
  groups,
  selected,
  onChange,
  placeholder: _placeholder = "Sélectionner...",
  searchable = false,
  emptyMessage = "Aucun résultat",
  className,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // Flatten all options for searching
  const allOptions = React.useMemo(() => {
    if (options) {
      return [...options].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    }
    if (groups) {
      return groups.flatMap(g => 
        [...g.options].sort((a, b) => a.label.localeCompare(b.label, 'fr'))
      );
    }
    return [];
  }, [options, groups]);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!search) return null;
    const searchLower = search.toLowerCase();
    return allOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchLower)
    );
  }, [search, allOptions]);

  // Sort groups with alphabetically sorted options
  const sortedGroups = React.useMemo(() => {
    if (!groups) return null;
    return groups.map(g => ({
      ...g,
      options: [...g.options].sort((a, b) => a.label.localeCompare(b.label, 'fr'))
    }));
  }, [groups]);

  // Sort options alphabetically
  const sortedOptions = React.useMemo(() => {
    if (!options) return null;
    return [...options].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [options]);

  const handleToggle = (value: string) => {
    const updated = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(updated);
  };

  const handleSelectAll = () => {
    const allValues = allOptions.map(o => o.value);
    const allSelected = allValues.every(v => selected.includes(v));
    if (allSelected) {
      onChange(selected.filter(v => !allValues.includes(v)));
    } else {
      onChange([...new Set([...selected, ...allValues])]);
    }
  };

  const handleClearAll = () => {
    const allValues = allOptions.map(o => o.value);
    onChange(selected.filter(v => !allValues.includes(v)));
  };

  const allSelected = allOptions.length > 0 && allOptions.every(o => selected.includes(o.value));
  const someSelected = selected.length > 0 && allOptions.some(o => selected.includes(o.value));

  const renderOption = (option: MultiSelectOption) => {
    const isSelected = selected.includes(option.value);
    return (
      <CommandItem
        key={option.value}
        value={option.label}
        onSelect={() => handleToggle(option.value)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <Checkbox
          checked={isSelected}
          className="pointer-events-none"
        />
        <span className="flex-1">{option.label}</span>
        {isSelected && <Check className="h-4 w-4 text-primary" />}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-10 font-normal",
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
            <span className="truncate">{label}</span>
            {selected.length > 0 && (
              <Badge variant="secondary" className="ml-1 shrink-0 h-5 px-1.5 text-xs">
                {selected.length}
              </Badge>
            )}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[280px] p-0 bg-popover border shadow-md z-[100]" 
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        style={{ 
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Command shouldFilter={false}>
          {searchable && (
            <CommandInput 
              placeholder={`Rechercher...`}
              value={search}
              onValueChange={setSearch}
            />
          )}
          
          {/* Actions row */}
          <div className="flex items-center justify-between px-2 py-1.5 border-b bg-muted/50">
            <button
              onClick={handleSelectAll}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
            {someSelected && (
              <button
                onClick={handleClearAll}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                Effacer
              </button>
            )}
          </div>

          <CommandList 
            className="max-h-[250px] overflow-y-auto overscroll-contain"
            style={{
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain'
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            
            {/* Search results */}
            {filteredOptions && (
              <CommandGroup>
                {filteredOptions.map(renderOption)}
              </CommandGroup>
            )}
            
            {/* Normal display (no search) */}
            {!filteredOptions && sortedOptions && (
              <CommandGroup>
                {sortedOptions.map(renderOption)}
              </CommandGroup>
            )}
            
            {/* Grouped display */}
            {!filteredOptions && sortedGroups && sortedGroups.map((group, index) => (
              <React.Fragment key={group.id}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={group.label}>
                  {group.options.map(renderOption)}
                </CommandGroup>
              </React.Fragment>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
